import React, { createContext, useContext, useEffect, useState } from 'react';

export interface DynamicSettings {
  theme: 'light' | 'dark';
  language: string;
  itemsPerPage: number;
  autoRefreshInterval: number;
  showNotifications: boolean;
  compactMode: boolean;
  dateFormat: string;
  timeFormat: string;
  currency: string;
}

interface DynamicSettingsContextType {
  settings: DynamicSettings;
  updateSetting: <K extends keyof DynamicSettings>(key: K, value: DynamicSettings[K]) => void;
  resetSettings: () => void;
  loading: boolean;
}

const DEFAULT_SETTINGS: DynamicSettings = {
  theme: 'light',
  language: 'en',
  itemsPerPage: 10,
  autoRefreshInterval: 30000,
  showNotifications: true,
  compactMode: false,
  dateFormat: 'MM/dd/yyyy',
  timeFormat: '24h',
  currency: 'USD',
};

const DynamicSettingsContext = createContext<DynamicSettingsContextType | undefined>(undefined);

export const DynamicSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<DynamicSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);

  // Load settings from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem('dynamicSettings');
      if (stored) {
        const parsed = JSON.parse(stored);
        setSettings({ ...DEFAULT_SETTINGS, ...parsed });
      }
    } catch (err) {
      console.error('Failed to load settings from localStorage:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateSetting = <K extends keyof DynamicSettings>(key: K, value: DynamicSettings[K]): void => {
    setSettings((prev) => {
      const updated = { ...prev, [key]: value };
      try {
        localStorage.setItem('dynamicSettings', JSON.stringify(updated));
      } catch (err) {
        console.error('Failed to save settings to localStorage:', err);
      }
      return updated;
    });
  };

  const resetSettings = (): void => {
    setSettings(DEFAULT_SETTINGS);
    try {
      localStorage.removeItem('dynamicSettings');
    } catch (err) {
      console.error('Failed to reset settings:', err);
    }
  };

  const value: DynamicSettingsContextType = {
    settings,
    updateSetting,
    resetSettings,
    loading,
  };

  return (
    <DynamicSettingsContext.Provider value={value}>{children}</DynamicSettingsContext.Provider>
  );
};

export const useDynamicSettings = (): DynamicSettingsContextType => {
  const context = useContext(DynamicSettingsContext);
  if (context === undefined) {
    throw new Error('useDynamicSettings must be used within a DynamicSettingsProvider');
  }
  return context;
};

